import { getCurrentFirebaseUser } from './client'
import { getHanaspeakApiBaseUrl } from './functions-api'
import {
  FLIPBOOK_ANNOTATION_SCHEMA_VERSION,
  type FlipbookAnnotationPage,
  type FlipbookAnnotationState,
  type FlipbookAnnotationStroke,
  type FlipbookTextAnnotation,
} from '@features/learning/books/flipbook/flipbook-annotation-contracts'
import type { BookPdfSlot } from './book-pdf-reader'

export interface FlipbookAnnotationIdentity {
  readonly bookId: string
  readonly revision: string
  readonly slot: BookPdfSlot
}

export interface FlipbookAnnotationSnapshot {
  readonly identity: FlipbookAnnotationIdentity
  readonly state: FlipbookAnnotationState
}

export interface FlipbookAnnotationRepository {
  readonly load: (identity: FlipbookAnnotationIdentity) => Promise<FlipbookAnnotationSnapshot>
  readonly savePage: (input: {
    readonly identity: FlipbookAnnotationIdentity
    readonly page: FlipbookAnnotationPage
  }) => Promise<unknown>
}

export class FlipbookAnnotationApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'FlipbookAnnotationApiError'
    this.status = status
  }
}

function requireReaderUser() {
  const user = getCurrentFirebaseUser()
  if (!user) {
    throw new FlipbookAnnotationApiError('Sign in is required.', 403)
  }
  return user
}

async function readApiPayload(response: Response) {
  return response.json().catch(() => null) as Promise<unknown>
}

function getErrorMessage(payload: unknown, fallback: string) {
  const candidate = payload as { error?: unknown } | null
  return typeof candidate?.error === 'string' && candidate.error.trim() ? candidate.error : fallback
}

async function getAuthorizationHeaders() {
  const user = requireReaderUser()
  return {
    Authorization: `Bearer ${await user.getIdToken()}`,
    'Content-Type': 'application/json',
  }
}

function normalizePage(value: unknown): FlipbookAnnotationPage | null {
  const candidate = value as Partial<FlipbookAnnotationPage> | null
  const page = candidate?.page
  if (!candidate || typeof page !== 'number' || !Number.isSafeInteger(page) || page <= 0) {
    return null
  }

  return {
    page,
    strokes: Array.isArray(candidate.strokes) ? candidate.strokes as readonly FlipbookAnnotationStroke[] : [],
    texts: Array.isArray(candidate.texts) ? candidate.texts as readonly FlipbookTextAnnotation[] : [],
  }
}

function pagesToState(pages: readonly FlipbookAnnotationPage[]): FlipbookAnnotationState {
  return pages.reduce<FlipbookAnnotationState>((state, page) => ({
    strokesByPage: page.strokes.length > 0 ? { ...state.strokesByPage, [page.page]: page.strokes } : state.strokesByPage,
    textsByPage: page.texts.length > 0 ? { ...state.textsByPage, [page.page]: page.texts } : state.textsByPage,
  }), { strokesByPage: {}, textsByPage: {} })
}

function buildAnnotationsUrl(identity: Pick<FlipbookAnnotationIdentity, 'bookId' | 'slot' | 'revision'>) {
  const query = new URLSearchParams({
    bookId: identity.bookId,
    revision: identity.revision,
    slot: identity.slot,
  })
  return `${getHanaspeakApiBaseUrl()}/learning/books/pdf/annotations?${query.toString()}`
}

async function requestJson(response: Response, fallback: string) {
  const payload = await readApiPayload(response)
  if (!response.ok) {
    throw new FlipbookAnnotationApiError(getErrorMessage(payload, fallback), response.status)
  }
  return payload
}

export async function loadFlipbookAnnotations(identity: FlipbookAnnotationIdentity): Promise<FlipbookAnnotationSnapshot> {
  const response = await fetch(buildAnnotationsUrl(identity), {
    headers: await getAuthorizationHeaders(),
    method: 'GET',
  })
  const payload = await requestJson(response, 'Flipbook annotations could not be loaded.') as {
    bookId?: unknown
    pages?: unknown
    revision?: unknown
    schemaVersion?: unknown
    slot?: unknown
  }
  const pages = Array.isArray(payload.pages) ? payload.pages.map(normalizePage).filter((page): page is FlipbookAnnotationPage => page !== null) : []
  if (
    payload.schemaVersion !== FLIPBOOK_ANNOTATION_SCHEMA_VERSION ||
    payload.bookId !== identity.bookId ||
    payload.slot !== identity.slot ||
    payload.revision !== identity.revision
  ) {
    throw new FlipbookAnnotationApiError('Flipbook annotations returned an invalid identity.', 502)
  }

  return {
    identity,
    state: pagesToState(pages),
  }
}

export async function saveFlipbookAnnotationPage({
  identity,
  page,
}: {
  readonly identity: FlipbookAnnotationIdentity
  readonly page: FlipbookAnnotationPage
}) {
  const response = await fetch(`${getHanaspeakApiBaseUrl()}/learning/books/pdf/annotations/page`, {
    body: JSON.stringify({
      bookId: identity.bookId,
      page: page.page,
      revision: identity.revision,
      slot: identity.slot,
      strokes: page.strokes,
      texts: page.texts,
    }),
    headers: await getAuthorizationHeaders(),
    method: 'PUT',
  })
  return requestJson(response, 'Flipbook annotations could not be saved.')
}

export async function clearFlipbookAnnotations(identity: FlipbookAnnotationIdentity) {
  const response = await fetch(`${getHanaspeakApiBaseUrl()}/learning/books/pdf/annotations`, {
    body: JSON.stringify(identity),
    headers: await getAuthorizationHeaders(),
    method: 'DELETE',
  })
  return requestJson(response, 'Flipbook annotations could not be cleared.')
}

export const flipbookAnnotationRepository: FlipbookAnnotationRepository = {
  load: loadFlipbookAnnotations,
  savePage: saveFlipbookAnnotationPage,
}
